import { createState } from "@/state.init";
import { SEED, AdminUser, UserStatus, UserRole } from "./models";
import { UseLoading } from "@/common/composition/loading";
import * as api from "./api";
import { SortBy, SORTING_DIR } from "@/common/models";
import { difference, sortBy } from "@/fp";
import { AppAnalytics } from "@/analytics";
import { ToastType, UseToast } from "@/components/root/toast/toast.state";
import { ICONS } from "@/common/icons.map";
import {
  Restriction,
  restriction_to_info_type_code,
} from "@/common/models/admin-restrictions.model";
import { UseToggle } from "@/common/composition/toggle-item";
import { UseModal } from "@/components/root/modal";

const [display_as_table, , set_display_as_table] = UseToggle(false);
const { isLoading, setLoading } = UseLoading(false);

const { getter, mutation, m, action } = createState("admins", SEED);

//#region  === TOTAL COUNT ===
const totalCount = getter((s) => s.totalCount);
const setTotalCount = m.set("totalCount");
//#endregion

//#region === ADMINS ===
const admins = getter((s) => s.admins);
const removed_admins = getter((s) =>
  s.admins.filter((d) => d.status == UserStatus.BLOCKED)
);
const setAdmins = m.set("admins");
const update_admin = m.updateOneBy<AdminUser>("admins", "id");
const get_admin_role = (a: AdminUser): [UserRole, string] =>
  a.restrictions.length > 0
    ? [UserRole.BUSINESS_ADMIN, "Business Unit Admin"]
    : [UserRole.SYSTEM_ADMIN, "Super Admin"];
const getAdmins = action<void>("get admins", async ({ mutate, gState }) => {
  setLoading(true);
  const res = await api.getAdmins();
  const keep_info_types = difference(
    Object.keys(restriction_to_info_type_code)
  );
  const to_into_type_icon = (id: string) =>
    ICONS.L0_ICONS.get(restriction_to_info_type_code[id]);
  if (res.success) {
    mutate(
      setAdmins,
      res.value.map((adm) => {
        const [role, role_display_name] = get_admin_role(adm);
        return {
          ...adm,
          hide_menu: adm.email === gState.shared.user.email,
          role,
          info_types: keep_info_types(adm.restrictions).map(to_into_type_icon),
          role_display_name,
        };
      })
    );
    mutate(setTotalCount, res.value.length);
  }
  setLoading(false);
});
//#endregion

//#region === ACTIONS ===
const {
  isLoading: isPerformingAction,
  setLoading: setIsPerformingAction,
} = UseLoading(false);

const changeStatus = action<AdminUser>(
  "remove admin",
  async ({ mutate, state }, admin) => {
    setIsPerformingAction(true);
    const is_delete_res = await UseModal().showSimple({
      is_destructive: true,
      title: "Remove Admin",
      desc:
        "Are you sure you want to remove this admin? </br> to restore this admin you will need to invite him/her again",
      submitBtnText: "Remove",
    });
    if (is_delete_res.type == "canceled") {
      return;
    }
    const isActive = admin.status === UserStatus.ACTIVE;
    const statusToSet = isActive ? UserStatus.BLOCKED : UserStatus.ACTIVE;

    AppAnalytics.report({
      component: "Admins",
      eventName: `Cognni_Admin[Action:${isActive ? "Block" : "Activate"}]`,
    });

    const res = await api.changeAdminStatus({
      id: admin.id,
      status: statusToSet,
    });
    if (res.success) {
      mutate(
        setAdmins,
        state.admins.map((a) => {
          if (a.id === admin.id) {
            a.status = statusToSet;
          }
          return a;
        })
      );
    }
    setIsPerformingAction(false);
  }
);

const restore_removed_admins = async (emails: string[]) => {
  const admins_to_restore = removed_admins.value.filter((a) =>
    emails.includes(a.email)
  );
  if (admins_to_restore.length == 0) {
    return;
  }
  for (const admin of admins_to_restore) {
    const res = await api.changeAdminStatus({
      id: admin.id,
      status: UserStatus.ACTIVE,
    });
  }
  getAdmins();
};
//#endregion

//#region === SORT ===
const setSortBy = mutation<SortBy>("set sort by", (s, sortBy) => {
  return {
    ...s,
    sortBy,
  };
});
const onSortingChanged = action<SortBy>(
  "on sort changed",
  async ({ mutate, state }, { dir, key }) => {
    mutate(setSortBy, { dir, key });
    mutate(
      setAdmins,
      sortBy(state.admins, key as keyof AdminUser, dir === SORTING_DIR.ASC)
    );
  }
);
//#endregion

//#region update restrictions
const update_admin_restrictions = action<{
  admin: AdminUser;
  role: UserRole;
  restrictions: Restriction[];
}>(
  "update admin restrictions",
  async ({ mutate }, { restrictions, role, admin }) => {
    setLoading(true);
    if (role === UserRole.BUSINESS_ADMIN && restrictions.length === 0) {
      restrictions = ["ar01"];
    }
    const res = await api.update_restrictions({ restrictions, admin });
    if (res.success) {
      mutate(update_admin, {
        ...admin,
        role,
        role_display_name:
          role === UserRole.BUSINESS_ADMIN
            ? "Business Unit Admin"
            : "Super Admin",
        restrictions,
      });
    } else {
      UseToast().show({
        type: ToastType.ALERT,
        msg: "failed to change admin's available scope",
        duration: 5000,
      });
    }
    setLoading(false);
  }
);
//#endregion

export {
  isLoading,
  display_as_table,
  set_display_as_table,
  admins,
  getAdmins,
  totalCount,
  // removed admins
  removed_admins,
  restore_removed_admins,
  // actions
  isPerformingAction,
  changeStatus,
  onSortingChanged,
  update_admin_restrictions,
};
